"use client";

import { useNotificationsWS } from "@/hooks/use-notifications-ws";
import {
  InAppNotification,
  NotificationStyle,
} from "@/lib/client/notifications-api";
import { createContext, useCallback, useContext, useState } from "react";
import { toast } from "sonner";

/**
 * Notification Listener Context
 *
 * Provides global notification handling based on notification_style:
 * - "toast": Show a sonner toast notification
 * - "bell_open": Signal to open the notification bell popover
 * - "bell_badge": Just update the badge (silent)
 */

interface NotificationListenerContextType {
  /** Signal to open the notification bell (set by bell_open style notifications) */
  shouldOpenBell: boolean;
  /** Clear the open bell signal */
  clearOpenBell: () => void;
  /** Last notification that triggered bell_open */
  highlightedNotification: InAppNotification | null;
  /** Clear the highlighted notification */
  clearHighlightedNotification: () => void;
}

const NotificationListenerContext =
  createContext<NotificationListenerContextType | null>(null);

export function NotificationListenerProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [shouldOpenBell, setShouldOpenBell] = useState(false);
  const [highlightedNotification, setHighlightedNotification] =
    useState<InAppNotification | null>(null);

  const handleNotification = useCallback((notification: InAppNotification) => {
    const style: NotificationStyle = notification.notification_style || "toast";

    // Derive display title and message from the updated interface
    const displayTitle =
      notification.display_name ||
      notification.title ||
      notification.message ||
      "Notification";

    const displayMessage =
      notification.notification_type === "alarm"
        ? notification.alarm_message || notification.message
        : notification.message;

    switch (style) {
      case "toast":
        // Show a sonner toast notification
        const severity = notification.severity;
        const isCritical = severity === "critical" || severity === "major";

        // Use different toast types based on severity
        if (isCritical) {
          toast.error(displayTitle, {
            description: displayMessage,
            duration: 10000, // 10 seconds for critical
          });
        } else {
          toast.info(displayTitle, {
            description: displayMessage,
            duration: 5000, // 5 seconds for others
          });
        }
        break;

      case "bell_open":
        // Signal to open the notification bell
        setShouldOpenBell(true);
        setHighlightedNotification(notification);
        break;

      case "bell_badge":
        // Silent - the unread count is already updated by useNotificationsWS
        // No action needed here
        break;
    }
  }, []);

  // Subscribe to notifications
  useNotificationsWS({
    onNotificationReceived: handleNotification,
  });

  const clearOpenBell = useCallback(() => {
    setShouldOpenBell(false);
  }, []);

  const clearHighlightedNotification = useCallback(() => {
    setHighlightedNotification(null);
  }, []);

  return (
    <NotificationListenerContext.Provider
      value={{
        shouldOpenBell,
        clearOpenBell,
        highlightedNotification,
        clearHighlightedNotification,
      }}
    >
      {children}
    </NotificationListenerContext.Provider>
  );
}

/**
 * Hook to access the notification listener context
 */
export function useNotificationListener(): NotificationListenerContextType {
  const context = useContext(NotificationListenerContext);
  if (!context) {
    throw new Error(
      "useNotificationListener must be used within NotificationListenerProvider"
    );
  }
  return context;
}

/**
 * Hook to check if the notification bell should be opened
 * Returns shouldOpenBell and clearOpenBell
 */
export function useBellOpenSignal(): {
  shouldOpenBell: boolean;
  clearOpenBell: () => void;
  highlightedNotification: InAppNotification | null;
  clearHighlightedNotification: () => void;
} {
  const context = useContext(NotificationListenerContext);
  return {
    shouldOpenBell: context?.shouldOpenBell ?? false,
    clearOpenBell: context?.clearOpenBell ?? (() => {}),
    highlightedNotification: context?.highlightedNotification ?? null,
    clearHighlightedNotification:
      context?.clearHighlightedNotification ?? (() => {}),
  };
}
